import React, { useCallback } from "react";
import { SelectOption } from "@/types";
import {
  Box,
  MenuItem,
  Typography,
  Divider,
  MenuList,
  styled,
} from "@mui/material";
import { CustomCheckbox } from "./Checkbox";

export interface CheckboxListProps<V extends string | number = string> {
  options: SelectOption<V>[];
  selected: Array<V>;
  setSelected: (selected: Array<V>) => void;
  maxHeight?: number | string;
}

export function CheckboxList<V extends string | number = string>({
  selected,
  options,
  setSelected,
  maxHeight = 300,
}: CheckboxListProps<V>) {
  const handleToggleItem = useCallback(
    (value: V) =>
      setSelected(
        selected.includes(value)
          ? selected.filter((item) => item !== value)
          : selected.concat(value)
      ),
    [setSelected, selected]
  );

  const handleSelectAll = useCallback(() => {
    const selectAllOptions =
      selected.length === options.length
        ? []
        : options.map((option) => option.value);
    setSelected(selectAllOptions);
  }, [options, selected, setSelected]);

  const isAllSelected = selected.length === options.length;

  return (
    <Box maxHeight={maxHeight} overflow="auto">
      <MenuList>
        <StyledMenuItem onClick={handleSelectAll}>
          <Typography>Select All</Typography>
          <CustomCheckbox
            checked={isAllSelected}
            indeterminate={selected.length > 0 && !isAllSelected}
            isParent
          />
        </StyledMenuItem>
        <Divider component="span" />
        {options.map((option) => (
          <StyledMenuItem
            key={option.value}
            onClick={() => handleToggleItem(option.value)}
          >
            <Typography>{option.label}</Typography>
            <CustomCheckbox checked={selected.includes(option.value)} />
          </StyledMenuItem>
        ))}
      </MenuList>
    </Box>
  );
}

const StyledMenuItem = styled(MenuItem)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});
